import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { authClient } from '../lib/auth-client';
import { clearBearer, getBearer, isDesktop } from '../lib/desktop-token';
import { AuthScreen } from './auth/AuthScreen';
import { authPersist } from './auth/authShared';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { data: session, isPending, error } = authClient.useSession();

    // Distinguish "signed out" from "couldn't ask". A dead/invalid credential
    // does NOT error — the api answers /get-session with HTTP 200 and a null
    // body — so a RESOLVED NULL session is the real sign-out signal. An `error`
    // means the request itself failed (offline, DNS, 5xx, api restarting), and
    // evicting on that logs out a perfectly valid user; better-auth re-validates
    // on every window focus, so one alt-tab during a blip used to be enough.
    // Keep the last good session across those, and only drop it on a true 401.
    const [lastGood, setLastGood] = useState<typeof session | null>(null);
    useEffect(() => {
        if (session) setLastGood(session);
        // A RESOLVED null without error IS the sign-out signal — drop the
        // snapshot with it. Keeping it would let a later transport error
        // (api restarting during a focus revalidation) resurrect the
        // signed-out user's UI from stale state.
        else if (!isPending && !error) setLastGood(null);
    }, [session, isPending, error]);

    const unauthorized =
        (error as { status?: number } | null | undefined)?.status === 401;
    const transportError = !!error && !unauthorized;
    const effectiveSession = session ?? (transportError ? lastGood : null);

    // A resolved-null session means the stored bearer is no longer valid, and
    // nothing else ever clears it (only explicit sign-out does). Drop it so the
    // next launch starts clean instead of retrying a dead token forever.
    const staleToken =
        !isPending && !error && !session && isDesktop() && !!getBearer();
    useEffect(() => {
        if (staleToken) clearBearer();
    }, [staleToken]);

    // better-auth re-validates the session on window focus (alt-tab) and just
    // after sign-up, which briefly flips isPending. We must only show the
    // full-screen spinner on the FIRST resolution — otherwise every refetch
    // unmounts <AuthScreen> and wipes its view state (e.g. the OTP step snaps
    // back to the sign-in form). Once we've resolved once, keep the screen
    // mounted through background refetches.
    const [everResolved, setEverResolved] = useState(false);
    useEffect(() => {
        if (!isPending) setEverResolved(true);
    }, [isPending]);

    // Authenticated: drop any stale auth-screen step so the next sign-out
    // starts at the sign-in form, not a half-finished verify step.
    const authed = !!effectiveSession;
    useEffect(() => {
        if (authed) authPersist.clear();
    }, [authed]);

    if (isPending && !everResolved) {
        return (
            <div className="min-h-screen w-full grid place-items-center bg-bg-0">
                <div className="flex flex-col items-center gap-3">
                    <img
                        src="/cotty-logo.svg"
                        alt="cotty"
                        className="w-9 h-9 opacity-90"
                    />
                    <Loader2
                        size={20}
                        className="animate-spin text-text-muted"
                        aria-label="Loading session"
                    />
                </div>
            </div>
        );
    }

    if (!effectiveSession) {
        return <AuthScreen />;
    }

    return <>{children}</>;
};

export default AuthGuard;
